import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useFormToken } from './useFormToken.js';
import {
  Box,
  Button,
  Input,
  Label,
  FormGroup,
  Text,
  Icon,
  FormMessage,
} from '@adminjs/design-system';

const AV_REQUEST_GROUP_NAME = 'AV Request';

type AVRequestFormPageProps = {
  onBack: () => void;
};

type AVRequestFields = {
  submitterName: string;
  submitterEmail: string;
  submitterPhone: string;
  submitterDepartment: string;
  groupId: string;
  function: string;
  venue: string;
  eventFrom: string;
  eventTo: string;
  audioEquipment: string[];
  videoEquipment: string[];
  other: string;
};

const AUDIO_EQUIPMENT_OPTIONS = [
  'Amplifier',
  'Podium Mike',
  'Collar Mike (Will be given if available)',
  'Handheld Mike',
];

const VIDEO_EQUIPMENT_OPTIONS = [
  'LCD Projector',
];

function AVRequestFormPage({ onBack }: AVRequestFormPageProps) {
  const [avRequestGroupId, setAvRequestGroupId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [requestId, setRequestId] = useState<number | null>(null);
  const [groupsError, setGroupsError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const { token, tokenError } = useFormToken();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    getValues,
    formState: { errors },
  } = useForm<AVRequestFields>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: false,
  });

  useEffect(() => {
    async function fetchAVRequestGroup() {
      setGroupsError(null);
      try {
        const res = await fetch('/api/groups');
        if (!res.ok) throw new Error('Failed to fetch AVRequest group');
        const data = await res.json();
        const groups = Array.isArray(data) ? data : [];
        const avRequestGroup = groups.find((g: any) => g.name === AV_REQUEST_GROUP_NAME);

        if (!avRequestGroup) {
          throw new Error(`AVRequest group not found. Please contact IT support.`);
        }

        setAvRequestGroupId(avRequestGroup.id);
      } catch (err: any) {
        setGroupsError(err.message || 'Error loading AVRequest group');
      }
    }
    fetchAVRequestGroup();
  }, []);

  const countWords = (text: string): number => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  // Helper to format date for datetime-local input (local time, not UTC)
  const formatLocalDateTime = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Calculate minimum date (24 hours from now)
  const minDateTime = useMemo(() => {
    const twentyFourHoursFromNow = new Date();
    twentyFourHoursFromNow.setTime(twentyFourHoursFromNow.getTime() + 24 * 60 * 60 * 1000);
    return formatLocalDateTime(twentyFourHoursFromNow);
  }, []);

  const onSubmit = async (data: AVRequestFields) => {
    setSubmitError(null);
    setLoading(true);

    if (!avRequestGroupId) {
      setSubmitError('AVRequest group not loaded. Please refresh the page.');
      setLoading(false);
      return;
    }

    // Collect checkbox values from form data
    const formElement = document.getElementById('av-request-form') as HTMLFormElement;
    const formData = new FormData(formElement);
    const audioEquipmentValues = Array.from(formData.getAll('audioEquipment')) as string[];
    const videoEquipmentValues = Array.from(formData.getAll('videoEquipment')) as string[];

    try {
      const response = await fetch('/api/av-requests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          submitterName: data.submitterName,
          submitterEmail: data.submitterEmail,
          submitterPhone: data.submitterPhone,
          submitterDepartment: data.submitterDepartment,
          function: data.function,
          venue: data.venue,
          eventFrom: new Date(data.eventFrom).toISOString(),
          eventTo: new Date(data.eventTo).toISOString(),
          audioEquipment: audioEquipmentValues,
          videoEquipment: videoEquipmentValues,
          other: data.other || '',
          groupId: avRequestGroupId,
          _formToken: token,
          'cf-turnstile-response': turnstileToken,
        }),
      });

      if (!response.ok) {
        let result = {};
        try { result = await response.json(); } catch { }

        // Handle server-side validation errors
        if ((result as any)?.errors) {
          const serverErrors = (result as any).errors;
          Object.keys(serverErrors).forEach((key) => {
            setError(key as keyof AVRequestFields, {
              type: 'server',
              message: serverErrors[key].message || serverErrors[key],
            });
          });
        }

        setSubmitError((result as any)?.message || 'Failed to submit AV request.');
        setLoading(false);
        return;
      }

      const result = await response.json();
      setSuccess(true);
      setRequestId(result.id);
    } catch (err: any) {
      setSubmitError(err.message || 'Network error.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      {/* Header */}
      <Box
        style={{
          background: 'linear-gradient(135deg, #047857 0%, #059669 100%)',
          borderBottom: '4px solid #10b981',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}
      >
        <Box
          p="xl"
          style={{ maxWidth: '800px', margin: '0 auto' }}
        >
          <Box display="flex" alignItems="center" style={{ gap: '16px' }}>
            <Button
              variant="text"
              onClick={onBack}
              style={{ color: 'rgba(255,255,255,0.8)', padding: '8px 12px' }}
            >
              <Icon icon="ChevronLeft" /> Back
            </Button>
            <Text fontSize="h3" fontWeight="bold" style={{ color: 'white' }}>
              Audio-Visual Equipment Request
            </Text>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box p="xl" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <Box
          bg="white"
          p="xl"
          style={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          }}
        >
          {/* Success State */}
          {success ? (
            <Box
              textAlign="center"
              p="xl"
              style={{
                background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                borderRadius: '12px',
                border: '2px solid #a7f3d0'
              }}
            >
              <Box
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  boxShadow: '0 4px 15px rgba(5, 150, 105, 0.3)'
                }}
              >
                <Text style={{ fontSize: '40px', color: 'white' }}>✓</Text>
              </Box>
              <Text fontSize="default" style={{ color: '#047857', marginBottom: '16px' }}>
                Your AV equipment request has been registered successfully!
              </Text>
              <Text style={{ color: '#6b7280', fontSize: '14px', marginBottom: '8px' }}>
                Please note your Request ID for reference:
              </Text>
              <Box
                mt="default"
                p="default"
                style={{
                  background: 'white',
                  borderRadius: '8px',
                  border: '2px solid #059669',
                  display: 'inline-block'
                }}
              >
                <Text fontSize="h2" fontWeight="bold" style={{ color: '#059669' }}>
                  #{requestId}
                </Text>
              </Box>
              <Box mt="xl">
                <Button variant="primary" onClick={onBack}>
                  Back to Home
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              {/* Notice */}
              <Box
                mb="lg"
                p="default"
                style={{
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                  borderRadius: '8px',
                  borderLeft: '4px solid #f59e0b'
                }}
              >
                <Text fontSize="sm" style={{ color: '#92400e' }}>
                  <strong>NOTE:</strong> Send the request at least <strong>24 Hours</strong> before the event. Please make your own arrangements for <strong>LAPTOP</strong> and UPS required for the LCD projection.
                </Text>
              </Box>

              {/* Error State */}
              {(submitError || tokenError || turnstileError) && (
                <Box
                  mb="lg"
                  p="lg"
                  style={{
                    background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                    borderRadius: '10px',
                    border: '2px solid #fca5a5',
                    boxShadow: '0 2px 8px rgba(220, 38, 38, 0.15)'
                  }}
                >
                  <Box display="flex" alignItems="center" style={{ gap: '12px' }}>
                    <Box
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: '#dc2626',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    >
                      ⚠
                    </Box>
                    <Text style={{ color: '#dc2626', fontWeight: 'bold', fontSize: '16px' }}>{submitError || tokenError || turnstileError}</Text>
                  </Box>
                </Box>
              )}

              {/* Form */}
              <form id="av-request-form" onSubmit={handleSubmit(onSubmit)}>
                <style>{`.frm-opt { clip: rect(0,0,0,0); position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0; border: 0; overflow: hidden; white-space: nowrap; }`}</style>
                <Box
                  mb="default"
                  pb="default"
                  style={{ borderBottom: '2px solid #e5e7eb' }}
                >
                  <Text fontSize="h4" fontWeight="bold" style={{ color: '#1f2937' }}>Contact Information</Text>
                </Box>

                <FormGroup error={!!errors.submitterName}>
                  <Label required style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>Name</Label>
                  <Input
                    variant="lg"
                    {...register('submitterName', {
                      required: 'Name is required',
                      minLength: { value: 3, message: 'Name must be at least 3 characters' },
                      maxLength: { value: 255, message: 'Name must be less than 255 characters' },
                    })}
                    placeholder="Your full name"
                    disabled={loading}
                    aria-invalid={!!errors.submitterName}
                    aria-describedby={errors.submitterName ? 'error-name' : undefined}
                    autoComplete="name"
                    style={{
                      fontSize: '15px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb'
                    }}
                  />
                  {errors.submitterName && <FormMessage>{errors.submitterName.message}</FormMessage>}
                </FormGroup>

                <FormGroup error={!!errors.submitterEmail} mt="lg">
                  <Label required style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>Email</Label>
                  <Input
                    variant="lg"
                    type="email"
                    {...register('submitterEmail', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Please enter a valid email address',
                      },
                      maxLength: { value: 255, message: 'Email must be less than 255 characters' },
                    })}
                    placeholder="your.email@example.com"
                    disabled={loading}
                    aria-invalid={!!errors.submitterEmail}
                    aria-describedby={errors.submitterEmail ? 'error-email' : undefined}
                    autoComplete="email"
                    style={{
                      fontSize: '15px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb'
                    }}
                  />
                  {errors.submitterEmail && <FormMessage>{errors.submitterEmail.message}</FormMessage>}
                </FormGroup>

                <FormGroup error={!!errors.submitterPhone} mt="lg">
                  <Label required style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>Intercom / Phone #</Label>
                  <Input
                    variant="lg"
                    type="tel"
                    {...register('submitterPhone', {
                      required: 'Phone number is required',
                      minLength: { value: 10, message: 'Phone number must be at least 10 digits' },
                      pattern: {
                        value: /^\d+$/,
                        message: 'Phone number must contain only digits',
                      },
                    })}
                    disabled={loading}
                    aria-invalid={!!errors.submitterPhone}
                    aria-describedby={errors.submitterPhone ? 'error-phone' : undefined}
                    autoComplete="tel"
                    style={{
                      fontSize: '15px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb'
                    }}
                  />
                  {errors.submitterPhone && <FormMessage>{errors.submitterPhone.message}</FormMessage>}
                </FormGroup>
                <FormGroup error={!!errors.submitterDepartment} mt="lg">
                  <Label required style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>Department</Label>
                  <Input
                    variant="lg"
                    {...register('submitterDepartment', {
                      required: 'Department is required',
                      minLength: { value: 2, message: 'Department must be at least 2 characters' },
                      maxLength: { value: 100, message: 'Department must be less than 100 characters' },
                    })}
                    placeholder="Your department"
                    disabled={loading}
                    aria-invalid={!!errors.submitterDepartment}
                    aria-describedby={errors.submitterDepartment ? 'error-department' : undefined}
                    autoComplete="organization"
                    style={{
                      fontSize: '15px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb'
                    }}
                  />
                  {errors.submitterDepartment && <FormMessage>{errors.submitterDepartment.message}</FormMessage>}
                </FormGroup>

                {groupsError && (
                  <Box
                    mt="lg"
                    p="default"
                    style={{
                      background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                      borderRadius: '8px',
                      border: '2px solid #fca5a5'
                    }}
                  >
                    <Text style={{ color: '#dc2626' }} fontSize="sm">{groupsError}</Text>
                  </Box>
                )}

                <Box
                  mt="xl"
                  mb="default"
                  pb="default"
                  style={{ borderBottom: '2px solid #e5e7eb' }}
                >
                  <Text fontSize="h4" fontWeight="bold" style={{ color: '#1f2937' }}>Event Details</Text>
                </Box>

                <FormGroup error={!!errors.function}>
                  <Label required style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>Event Name / Function</Label>
                  <Input
                    variant="lg"
                    {...register('function', {
                      required: 'Function name is required',
                      minLength: { value: 3, message: 'Function name must be at least 3 characters' },
                      maxLength: { value: 255, message: 'Function name must be less than 255 characters' },
                    })}
                    placeholder="e.g., Guest Lecture on AI"
                    disabled={loading}
                    aria-invalid={!!errors.function}
                    aria-describedby={errors.function ? 'error-function' : undefined}
                    style={{
                      fontSize: '15px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb'
                    }}
                  />
                  {errors.function && <FormMessage>{errors.function.message}</FormMessage>}
                </FormGroup>

                <FormGroup error={!!errors.venue} mt="lg">
                  <Label required style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>Venue</Label>
                  <Input
                    variant="lg"
                    {...register('venue', {
                      required: 'Venue is required',
                      minLength: { value: 2, message: 'Venue must be at least 2 characters' },
                      maxLength: { value: 255, message: 'Venue must be less than 255 characters' },
                    })}
                    placeholder="e.g., Auditorium A, Main Building"
                    disabled={loading}
                    aria-invalid={!!errors.venue}
                    aria-describedby={errors.venue ? 'error-venue' : undefined}
                    style={{
                      fontSize: '15px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb'
                    }}
                  />
                  {errors.venue && <FormMessage>{errors.venue.message}</FormMessage>}
                </FormGroup>

                <FormGroup error={!!errors.eventFrom} mt="lg">
                  <Label required style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>Event Start Date & Time</Label>
                  <Input
                    variant="lg"
                    type="datetime-local"
                    {...register('eventFrom', {
                      required: 'Event start date is required',
                      validate: (value) => {
                        const eventStart = new Date(value);
                        const now = new Date();
                        const twentyFourHoursFromNow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
                        if (eventStart < twentyFourHoursFromNow) {
                          return 'Event must be scheduled at least 24 hours in advance';
                        }
                        return true;
                      },
                    })}
                    min={minDateTime}
                    disabled={loading}
                    aria-invalid={!!errors.eventFrom}
                    aria-describedby={errors.eventFrom ? 'error-eventFrom' : undefined}
                    style={{
                      fontSize: '15px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb'
                    }}
                  />
                  {errors.eventFrom && <FormMessage>{errors.eventFrom.message}</FormMessage>}
                </FormGroup>

                <FormGroup error={!!errors.eventTo} mt="lg">
                  <Label required style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>Event End Date & Time</Label>
                  <Input
                    variant="lg"
                    type="datetime-local"
                    {...register('eventTo', {
                      required: 'Event end date is required',
                      validate: (value) => {
                        if (!value) return true;

                        const eventEnd = new Date(value);
                        const now = new Date();
                        const twentyFourHoursFromNow = new Date(now.getTime() + 24 * 60 * 60 * 1000);

                        // Check 24-hour minimum first
                        if (eventEnd < twentyFourHoursFromNow) {
                          return 'Event must be scheduled at least 24 hours in advance';
                        }

                        // Then check if it's after start time
                        const eventFrom = getValues('eventFrom');
                        if (eventFrom) {
                          const start = new Date(eventFrom);
                          if (eventEnd <= start) {
                            const startFormatted = new Intl.DateTimeFormat('en-US', {
                              month: '2-digit',
                              day: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            }).format(start);
                            return `Event end must be after event start (${startFormatted})`;
                          }
                        }

                        return true;
                      },
                    })}
                    min={minDateTime}
                    disabled={loading}
                    aria-invalid={!!errors.eventTo}
                    aria-describedby={errors.eventTo ? 'error-eventTo' : undefined}
                    onInvalid={(e) => {
                      // Suppress browser's native validation tooltip
                      e.preventDefault();
                    }}
                    style={{
                      fontSize: '15px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb'
                    }}
                  />
                  {errors.eventTo && <FormMessage>{errors.eventTo.message}</FormMessage>}
                </FormGroup>

                <Box
                  mt="xl"
                  mb="default"
                  pb="default"
                  style={{ borderBottom: '2px solid #e5e7eb' }}
                >
                  <Text fontSize="h4" fontWeight="bold" style={{ color: '#1f2937' }}>Equipment Needed</Text>
                </Box>

                <FormGroup error={!!errors.audioEquipment}>
                  <Label style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>Audio Equipment</Label>
                  <Box
                    style={{
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      background: loading ? '#f3f4f6' : 'white'
                    }}
                  >
                    {AUDIO_EQUIPMENT_OPTIONS.map(equipment => (
                      <Box key={equipment} mb="sm" style={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          id={`audio-${equipment}`}
                          name="audioEquipment"
                          value={equipment}
                          disabled={loading}
                          style={{
                            width: '18px',
                            height: '18px',
                            marginRight: '10px',
                            cursor: loading ? 'not-allowed' : 'pointer'
                          }}
                        />
                        <Label
                          htmlFor={`audio-${equipment}`}
                          style={{
                            fontSize: '15px',
                            color: '#374151',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            margin: 0,
                            fontWeight: 'normal'
                          }}
                        >
                          {equipment}
                        </Label>
                      </Box>
                    ))}
                  </Box>
                  {errors.audioEquipment && <FormMessage>{errors.audioEquipment.message}</FormMessage>}
                </FormGroup>

                <FormGroup error={!!errors.videoEquipment} mt="lg">
                  <Label style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>Video Equipment</Label>
                  <Box
                    style={{
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      background: loading ? '#f3f4f6' : 'white'
                    }}
                  >
                    {VIDEO_EQUIPMENT_OPTIONS.map(equipment => (
                      <Box key={equipment} mb="sm" style={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          id={`video-${equipment}`}
                          name="videoEquipment"
                          value={equipment}
                          disabled={loading}
                          style={{
                            width: '18px',
                            height: '18px',
                            marginRight: '10px',
                            cursor: loading ? 'not-allowed' : 'pointer'
                          }}
                        />
                        <Label
                          htmlFor={`video-${equipment}`}
                          style={{
                            fontSize: '15px',
                            color: '#374151',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            margin: 0,
                            fontWeight: 'normal'
                          }}
                        >
                          {equipment}
                        </Label>
                      </Box>
                    ))}
                  </Box>
                  {errors.videoEquipment && <FormMessage>{errors.videoEquipment.message}</FormMessage>}
                </FormGroup>

                <FormGroup error={!!errors.other} mt="lg">
                  <Label style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>
                    Other Requirements / Notes
                  </Label>
                  <Input
                    variant="lg"
                    as="textarea"
                    {...register('other', {
                      validate: (value) => {
                        if (value) {
                          const wordCount = countWords(value);
                          if (wordCount > 100) {
                            return 'Notes must not exceed 100 words';
                          }
                        }
                        return true;
                      },
                    })}
                    placeholder="Any additional equipment or special requirements (max 100 words)..."
                    minRows={4}
                    disabled={loading}
                    aria-invalid={!!errors.other}
                    aria-describedby={errors.other ? 'error-other' : undefined}
                    style={{
                      fontSize: '15px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb'
                    }}
                  />
                  {errors.other && <FormMessage>{errors.other.message}</FormMessage>}
                </FormGroup>

                {/* Turnstile */}
                <Box mt="lg">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                    options={{ theme: 'light', size: 'normal' }}
                    onSuccess={(token) => { setTurnstileToken(token); setTurnstileError(null); }}
                    onError={() => setTurnstileError('Security check failed. Please try again.')}
                    onExpire={() => { setTurnstileToken(null); setTurnstileError(null); }}
                  />
                  {turnstileError && (
                    <Box mt="sm" display="flex" alignItems="center" style={{ gap: '8px' }}>
                      <Text style={{ color: '#dc2626', fontSize: '13px' }}>{turnstileError}</Text>
                      <button
                        type="button"
                        onClick={() => { setTurnstileError(null); turnstileRef.current?.reset(); }}
                        style={{
                          background: 'none',
                          border: '1px solid #dc2626',
                          borderRadius: '4px',
                          color: '#dc2626',
                          cursor: 'pointer',
                          fontSize: '12px',
                          padding: '2px 8px',
                        }}
                      >
                        Retry
                      </button>
                    </Box>
                  )}
                </Box>

                {/* Action Buttons */}
                <Box mt="xl" display="flex" justifyContent="flex-end" style={{ gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => { reset(); setSubmitError(null); }}
                    disabled={loading}
                    style={{
                      padding: '10px 24px',
                      fontSize: '14px',
                      fontWeight: 600,
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      background: 'white',
                      color: '#374151',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.6 : 1,
                    }}
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !token || !turnstileToken}
                    style={{
                      padding: '10px 24px',
                      fontSize: '14px',
                      fontWeight: 600,
                      borderRadius: '8px',
                      border: 'none',
                      background: (loading || !token || !turnstileToken) ? '#93c5fd' : '#1e40af',
                      color: 'white',
                      cursor: (loading || !token || !turnstileToken) ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {loading ? 'Submitting...' : (!token && !tokenError) ? 'Loading...' : 'Submit'}
                  </button>
                </Box>
              </form>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default AVRequestFormPage;
